import { updateLastVisitedOrgsCache } from '@hopin-team/dashboard-constants';
import { useDashboardReactiveMap } from '@hopin-team/dashboard-shared-react';
import { LDFlagSet } from 'launchdarkly-js-client-sdk';
import React, { ReactNode, useCallback, useEffect } from 'react';
import { Provider } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';

import { store } from '.';

export const StoreProvider = ({ children }: { children: ReactNode }) => {
  const reactiveMap = useDashboardReactiveMap();
  const location = useLocation();

  const setUser = useCallback(
    (user: { id: string; email: string } | undefined) => {
      if (!user || !user.email || !user.id) return;

      const { id, email } = store.getState().user;

      if (id === user.id && email === user.email) return;

      store.dispatch({
        type: 'reactive-map - user updated',
        payload: { ...user },
      });

      const organizationExternalId = store.getState().organizationExternalId;
      if (!organizationExternalId) return;

      updateLastVisitedOrgsCache(user.id, organizationExternalId);
    },
    [],
  );

  // Connect the dashboard's reactive map to the store
  reactiveMap.item('user_auth_token').listen(authToken => {
    store.dispatch({
      type: 'reactive-map - auth token updated',
      payload: { authToken },
    });
  });

  const updateFlags = useDebouncedCallback((featureFlags: LDFlagSet) => {
    store.dispatch({
      type: 'reactive-map - feature flags updated',
      payload: { featureFlags },
    });
  }, 250);

  reactiveMap.item('feature_flags').listen(updateFlags);
  reactiveMap.item('user').listen(setUser);

  const isOnEventWizardPage = window.location.pathname.includes(
    'create-event-wizard',
  );

  if (isOnEventWizardPage) {
    reactiveMap.item('snap').set('left');
  }

  const handleOrganizationChange = useCallback(
    (organizationExternalId: string | undefined) => {
      if (!organizationExternalId) return;

      store.dispatch({
        type: 'reactive-map - organization changed',
        payload: { organizationExternalId },
      });
      const user = store.getState().user;
      if (!user?.id || !user.email) return;

      updateLastVisitedOrgsCache(user.id, organizationExternalId);
    },
    [],
  );

  reactiveMap.item('org_id').listen(handleOrganizationChange);

  useEffect(() => {
    store.dispatch({
      type: 'router - route changed',
      payload: { pathname: location.pathname },
    });
  }, [location.pathname]);

  return <Provider store={store}>{children}</Provider>;
};
